import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref } from "vue"

const _hoisted_1 = { class: "main-container" }
const _hoisted_2 = ["data-id"]

import { ref, reactive, onMounted, getCurrentInstance, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useRoute, useRouter } from 'vue-router'
import { Plus, Close, Edit } from '@element-plus/icons-vue'
import type { FormInstance } from 'element-plus'
import Sortable from 'sortablejs'
import { getTopicConfigApi, saveTopicConfigApi } from '@/apis/house'
import { getSpecsApi, getProductDetail } from '@/apis/product'
import { downloadFile } from '@/utils'


export default /*@__PURE__*/_defineComponent({
  __name: 'TopicEdit',
  setup(__props) {

const { proxy } = getCurrentInstance() as any
const route = useRoute()
const router = useRouter()

// 表单引用
const formRef = ref<FormInstance>()

const previewList = ref<any>([])

// 表单数据（对应原PHP的$topic/$header/$activities等）
const formData = ref({
    id: '',
    template: 'topicV11',
    title: '',
    tags: '',
    share_title: '',
    share_content: '',
    image_path: '',
    image_id: '',
    ranking_type: 0, // 排行榜类型：1=排行榜专题（隐藏部分模块）
    delModuleIds: '',
    isid: 0,
    topicId: 0,
    // 头部设置列表
    header: [],
    // 活动设置
    activities: {
        bg_src: '',
        bgcolor: '',
        contents: []
    },
    // 功能设置列表
    funs: [
        []
    ],
    // 秒杀设置
    spike: {
        bgcolor: '',
        products: []
    },
    // 内容设置模块列表
    product_model: [
        {
            module_id: '',
            col_num:' 0',
            bgcolor: '',
            text: '',
            banners: [
                []
            ],
            products: []
        }
    ],
    // 头部设置列表
    footer: [],
})

// 表格引用
const headerTableRef = ref()
const activitiesTableRef = ref()
const funsTableRef = ref()
const spikeTableRef = ref()
const footerTableRef = ref()

const moduleBannerRefs = ref<any[]>([])
const setModuleBannerRef = (el: any, index: number) => {
    if (!el) return
    moduleBannerRefs.value[index] = el
    initSortable(el, () => formData.value.product_model[index]?.banners)
}

const moduleProductRefs = ref<any[]>([])
const setModuleProductRef = (el: any, index: number) => {
    if (!el) return
    moduleProductRefs.value[index] = el
    initSortable(el, () => formData.value.product_model[index]?.products)
}

/**
 * 初始化表格拖拽
 */
const initSortable = (tableInstance: any, getList: () => any[]) => {
    if (!tableInstance) return
    const el = tableInstance.$el.querySelector('.el-table__body-wrapper tbody') as any
    if (!el) return
    
    // 销毁旧实例防止重复绑定
    if (el.sortableInstance) {
        el.sortableInstance.destroy()
    }

    el.sortableInstance = Sortable.create(el, {
        animation: 150,
        draggable: 'tr',
        direction: 'vertical',
        fallbackOnBody: true,
        forceFallback: true,
        onEnd: ({ newIndex, oldIndex }: any) => {
            const list = getList()
            if (!list || newIndex === oldIndex) return
            const targetRow = list.splice(oldIndex, 1)[0]
            list.splice(newIndex, 0, targetRow)
        }
    })
}

const initAllSortables = () => {
    initSortable(headerTableRef.value, () => formData.value.header)
    initSortable(activitiesTableRef.value, () => formData.value.activities.contents)
    initSortable(funsTableRef.value, () => formData.value.funs)
    initSortable(spikeTableRef.value, () => formData.value.spike.products)
    initSortable(footerTableRef.value, () => formData.value.footer)

    // 内容模块的拖拽在 setModuleBannerRef / setModuleProductRef 中按渲染时机绑定
}

const getTopicInfo = async () => {
    const id = route.query.topic_id || ''
    const { code, data, msg } = await getTopicConfigApi(id) as any
    if (code != 200) return proxy.$message.error(msg)
    Object.assign(formData.value, initData(data))
    assignRowKeys()
    await nextTick()
    initAllSortables()
}
getTopicInfo()
const initData = (data) => {
    data.header = data.header || []

    if (!data.activities || data.activities.length == 0) {
        data.activities = {
            bg_src: '',
            bgcolor: '',
            contents: []
        }
    }

    if (!data.funs || data.funs.length == 0) {
        data.funs = [[]]
    }

    if (!data.spike || data.spike.length == 0) {
        data.spike = {
            bgcolor: '',
            products: []
        }
    }

    if (!data.product_model || data.product_model.length == 0) {
        data.product_model = [
            {
                id: '',
                col_num:' 0',
                bgcolor: '',
                text: '',
                banners: [
                    []
                ],
                products: []
            }
        ]
    }

    data.footer = data.footer || []
    
    return data;
}

// 初始化：模拟从后端获取数据（替代PHP的变量渲染）
onMounted(() => {
    assignRowKeys()
    initAllSortables()
})

const getSpecInfo = async (path: string, index: number, product_id: string) => {
    const { code, data, msg } = await getSpecsApi(product_id) as any
    if (code != 200) return proxy.$message.error(msg)
    getNestedValue(formData.value, path)[index].specs = data
}

const getProductInfo = async (path: string, index: number, product_id: string) => {
    const { code, data, msg } = await getProductDetail({product_id}) as any
    if (code != 200) return proxy.$message.error(msg)

    const { brand_name, price, status, title, specs } = data
    const productInfo = {
        brand_name,
        price,
        product_id,
        specs: specs || [],
        status,
        title,
    }
    Object.assign(getNestedValue(formData.value, path)[index], productInfo)
}

// ========== 通用方法 ==========
/**
 * 删除图片
 */
const delImg = () => {
    formData.value.image_path = ''
    formData.value.image_id = ''
}

/**
 * 图片上传成功回调
 */
const handleImgUploadSuccess = (response: any) => {
    formData.value.image_path = response.data.url
    formData.value.image_id = response.data.id
    ElMessage.success('图片上传成功')
}

/**
 * 仅允许输入数字
 */
const onlyNumber = (row: any, key: string) => {
    row[key] = row[key].replace(/\D/g, '')
}

/**
 * 删除表格行
 * @param path 数据路径（如：header / activities.contents）
 * @param index 行索引
 */
const delTr = (path: string, index: number) => {
    const arr = getNestedValue(formData.value, path)
    // if (arr.length <= 1) {
    //     ElMessage.warning('至少保留一行数据')
    //     return
    // }
    arr.splice(index, 1)
}

/**
 * 插入表格行
 * @param path 数据路径
 * @param index 插入位置
 */
const insertTr = (path: string, index: number) => {
    const arr = getNestedValue(formData.value, path)
    const emptyRow = getEmptyRowByPath(path)
    arr.splice(index, 0, emptyRow)
}

/**
 * 上移表格行
 * @param path 数据路径
 * @param index 行索引
 */
const moveUpTr = (path: string, index: number) => {
    if (index === 0) return
    const arr = getNestedValue(formData.value, path)
    ;[arr[index], arr[index - 1]] = [arr[index - 1], arr[index]]
}

/**
 * 下移表格行
 * @param path 数据路径
 * @param index 行索引
 */
const moveDownTr = (path: string, index: number) => {
    const arr = getNestedValue(formData.value, path)
    if (index === arr.length - 1) return
    ;[arr[index], arr[index + 1]] = [arr[index + 1], arr[index]]
}

/**
 * 添加表格行
 * @param path 数据路径
 */
const addTr = (path: string) => {
    const arr = getNestedValue(formData.value, path)
    const emptyRow = getEmptyRowByPath(path)
    arr.push(emptyRow)
}

/**
 * 获取嵌套对象的属性值
 * @param obj 源对象
 * @param path 路径（如：a.b.c）
 * @returns 属性值
 */
const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((acc, cur) => acc[cur], obj)
}

// 行唯一键生成与填充
const keySeed = ref(1)
const genKey = () => keySeed.value++
const assignRowKeys = () => {
    // 头部
    formData.value.header?.forEach((row: any) => row.__key ??= genKey())
    // 活动
    formData.value.activities?.contents?.forEach((row: any) => row.__key ??= genKey())
    // 功能（每行是数组）
    formData.value.funs?.forEach((row: any) => (row as any).__key ??= genKey())
    // 秒杀
    formData.value.spike?.products?.forEach((row: any) => row.__key ??= genKey())
    // 内容模块
    formData.value.product_model?.forEach((m: any) => {
        // banners 每行是数组
        m.banners?.forEach((row: any) => (row as any).__key ??= genKey())
        // products 每行是对象
        m.products?.forEach((row: any) => row.__key ??= genKey())
    })
    // 尾部
    formData.value.footer?.forEach((row: any) => row.__key ??= genKey())
}

/**
 * 根据路径获取空行模板
 * @param path 数据路径
 * @returns 空行对象
 */
const getEmptyRowByPath = (path: string) => {
    switch (path) {
        case 'header':
            return { __key: genKey(), src: '', url: '' }
        case 'activities.contents':
            return { __key: genKey(), banner_src: '', banner_url: '' }
        case 'funs':
            const funRow: any = [{ src: '', url: '', coupon_type: '', coupon_id: '' }]
            funRow.__key = genKey()
            return funRow
        case 'spike.products':
            return {
                __key: genKey(),
                product_id: '',
                brand_name: '',
                title: '',
                specs: [{ id: '', name: '', price: '' }],
                spec_id: '',
                price: '',
                status: '下架',
                image_path: '',
                sort: 1
            }
        default:
            if (path.indexOf('products') > -1) {
                return {
                    __key: genKey(),
                    id: '',
                    brand_name: '',
                    product_id: '',
                    title: '',
                    spec_name: '',
                    price: '',
                    status: '',
                    tag: '',
                    sort: 1,
                    image_path: '',
                    market_price: '',
                    spec_id: null,
                    tags: [],
                }
            }
            return {}
    }
}

/**
 * 根据路径获取行内空数据
 * @param path 数据路径
 */
const getEmptyInnerRowByPath = (path: string) => {
    if (path === 'funs') {
        return { src: '', url: '', coupon_type: '', coupon_id: '' }
    }
    if (path.indexOf('banners') > -1) {
        return { banner_src: '', banner_url: '' }
    }
    return {}
}

// 活动标签弹窗
const tagList = ref([
    { type: 1, img: 'https://dgss-oss-zs.oss-cn-beijing.aliyuncs.com/rests/rests_20230412150323_48.png', text: '', is_checked: false },
    { type: 2, img: 'https://dgss-oss-zs.oss-cn-beijing.aliyuncs.com/rests/rests_20230412150323_205.png', text: '', is_checked: false },
    { type: 3, img: 'https://dgss-oss-zs.oss-cn-beijing.aliyuncs.com/rests/rests_20230412150323_758.png', text: '', is_checked: false },
    { type: 4, img: 'https://dgss-oss-zs.oss-cn-beijing.aliyuncs.com/rests/rests_20230412150323_862.png', text: '', is_checked: false },
    { type: 5, name: '自定义', text: '', is_checked: false },
])
const tagDialogVisible = ref(false)
const currTagPath = ref<string>('')
const openTagDialog = (path, row) => {
    currTagPath.value = path
    if (row.tags && row.tags.length) {
        tagList.value.map((tag: any) => {
            const sameIdx = row.tags.findIndex((item: any) => tag.type === item.type)
            if (sameIdx > -1) {
                tag.is_checked = true
                tag.text = row.tags[sameIdx].text
            }
        })
    }
    tagDialogVisible.value = true
}
const onCheckedTagsChange = () => {
    let tmpTags:any = []
    tagList.value.map((tag: any) => {
        if (tag.is_checked) {
            tmpTags.push({ type: tag.type, text: tag.text, img: tag.img })
        }
    })
    
    getNestedValue(formData.value, currTagPath.value).tags = tmpTags
    resetTag()
    tagDialogVisible.value = false
}
const resetTag = () => {
    tagList.value.map((tag: any) => {
        tag.is_checked = false
        tag.text = ''
    })
}

// ========== 活动/秒杀/内容模块专属方法 ==========
/**
 * 规格变更回调
 */
const onSpecChange = (row: any) => {
    // const [specId, price] = row.spec_id.split('-')
    // row.price = price
}

/**
 * 商品排序打乱
 * @param path 数据路径
 */
const toSecondSort = (path: string) => {
    const arr = getNestedValue(formData.value, path)
    if (Array.isArray(arr)) {
        arr.sort(() => Math.random() - 0.5)
        ElMessage.success('排序已打乱')
    } else {
        ElMessage.warning('无法获取到需要打乱的数据')
    }
}

/**
 * 删除内容模块
 */
const delModule = (index: number, moduleId: string) => {
    ElMessageBox.confirm('确定删除该模块？', '提示', {
        type: 'warning'
    }).then(() => {
        formData.value.product_model.splice(index, 1)
        const existingIds = formData.value.delModuleIds || ''
        const newIds = existingIds 
            ? `${existingIds},${moduleId}` 
            : moduleId
        formData.value.delModuleIds = newIds
        nextTick(() => initAllSortables())
    })
}

/**
 * 上移内容模块
 */
const moveUpModule = (index: number) => {
    if (index === 0) return
    ;[formData.value.product_model[index], formData.value.product_model[index - 1]] = [
        formData.value.product_model[index - 1],
        formData.value.product_model[index]
    ]
    nextTick(() => initAllSortables())
}

/**
 * 下移内容模块
 */
const moveDownModule = (index: number) => {
    if (index === formData.value.product_model.length - 1) return
    ;[formData.value.product_model[index], formData.value.product_model[index + 1]] = [
        formData.value.product_model[index + 1],
        formData.value.product_model[index]
    ]
    nextTick(() => initAllSortables())
}

const addModule = () => {
    formData.value.product_model.push({
        module_id: '',
        col_num: '0',
        bgcolor: '',
        text: '',
        banners: [
            (() => { const r:any = [{ banner_src: '', banner_url: '' }]; r.__key = genKey(); return r })()
        ],
        products: []
    })
    nextTick(() => {
        initAllSortables()
    })
}

/**
 * 添加切图行
 */
const addInnerTr = (path: string, index: number) => {
    const arr = getNestedValue(formData.value, path)
    const row = arr[index]
    const emptyRow = getEmptyInnerRowByPath(path)
    
    if (Array.isArray(row)) {
        row.push(emptyRow)
    } else if (row && Array.isArray(row.items)) {
        row.items.push(emptyRow)
    } else {
        ElMessage.warning('无法添加行内数据')
    }
}

/**
 * 添加Banner行
 */
const addBannerTr = (banners: any[]) => {
    const row: any = [{ banner_src: '', banner_url: '' }]
    row.__key = genKey()
    banners.push(row)
}

/**
 * 删除Banner行
 */
const delBannerTr = (banners: any[], index: number) => {
    // if (banners.length <= 1) {
    //     ElMessage.warning('至少保留一行数据')
    //     return
    // }
    banners.splice(index, 1)
}

/**
 * 插入Banner行
 */
const insertBannerTr = (banners: any[], index: number) => {
    const row: any = [{ banner_src: '', banner_url: '' }]
    row.__key = genKey()
    banners.splice(index, 0, row)
}

/**
 * 上移Banner行
 */
const moveUpBannerTr = (banners: any[], index: number) => {
    if (index === 0) return
    ;[banners[index], banners[index - 1]] = [banners[index - 1], banners[index]]
}

/**
 * 下移Banner行
 */
const moveDownBannerTr = (banners: any[], index: number) => {
    if (index === banners.length - 1) return
    ;[banners[index], banners[index + 1]] = [banners[index + 1], banners[index]]
}

/**
 * 添加商品
 */
// const addProduct = (moduleId: string) => {
//   // 实际项目中可弹窗选择商品或输入商品ID
//     const module = formData.value.product_model.find(item => item.id === moduleId)
//     if (module) {
//         module.products.push({
//             __key: genKey(),
//             id: '',
//             image_path: '',
//             market_price: '',
//             spec_id: null,
//             tags: [],
//             brand_name: '',
//             product_id: '',
//             title: '',
//             spec_name: '',
//             price: '',
//             status: '',
//             tag: '',
//             sort: module.products.length + 1
//         })
//     }
// }


// 导出商品下单情况
const exportProductOrderDialog = ref(false)
const exportProductOrderFormData = ref<any>({})
const onExportProductOrder = (moduleId: string) => {
    exportProductOrderFormData.value.list_id = moduleId
    exportProductOrderFormData.value.dateRang = []
    exportProductOrderDialog.value = true
}
const exportProductOrder = () => {
    // 获取 product_model 下所有模块的商品ID
    const allProductIds: string[] = []
    formData.value.product_model.forEach((module: any) => {
        if (module.products && module.products.length > 0) {
            module.products.forEach((p: any) => {
                if (p.product_id) allProductIds.push(p.product_id)
            })
        }
    })

    // if (!exportProductOrderFormData.value.dateRang.length) return ElMessage.warning('请选择日期区间')
    if (!allProductIds.length) return ElMessage.warning('没有商品可导出')

    downloadFile('/opsapi/outputExcel', {
        idlist: allProductIds.join(','),
        timeRang: exportProductOrderFormData.value.dateRang.join(' - ')
    })
}

/**
 * 删除商品行
 */
const delProductTr = (products: any[], index: number) => {
    // if (products.length <= 1) {
    //     ElMessage.warning('至少保留一行数据')
    //     return
    // }
    products.splice(index, 1)
}

// ========== 表单提交 ==========
const onSubmit = async () => {
    const payload = JSON.parse(JSON.stringify(formData.value))
    if (Array.isArray(payload.product_model)) {
        payload.product_model.forEach((m:any) => {
            if (Array.isArray(m.products)) {
                m.products = m.products.map((p:any) => ({
                    product_id: p.product_id,
                    tags: p.tags || []
                }))
            }
        })
    }
    if (payload.spike && Array.isArray(payload.spike.products)) {
        payload.spike.products = payload.spike.products.map((p:any) => ({
            product_id: p.product_id,
            spec_id: p.spec_id,
            image_path: p.image_path
        }))
    }
    const { code, data, msg } = await saveTopicConfigApi({
        topic_data: JSON.stringify(payload)
    }) as any
    if (code != 200) return ElMessage.error(msg)
    ElMessage.success('保存成功')
    setTimeout(() => {
        router.go(-1)
    }, 1000)
}

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_image = _resolveComponent("el-image")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_text = _resolveComponent("el-text")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_checkbox = _resolveComponent("el-checkbox")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _directive_hex_color = _resolveDirective("hex-color")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_el_form, {
      ref_key: "formRef",
      ref: formRef,
      model: formData.value,
      "label-width": "100px",
      class: "form-edit"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_form_item, { label: "标题" }, {
          default: _withCtx(() => [
            _createVNode(_component_el_input, {
              modelValue: formData.value.title,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.title) = $event)),
              readonly: ""
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_form_item, { label: "分享标题：" }, {
          default: _withCtx(() => [
            _createVNode(_component_el_input, {
              modelValue: formData.value.share_title,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.share_title) = $event)),
              readonly: ""
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_form_item, { label: "分享内容" }, {
          default: _withCtx(() => [
            _createVNode(_component_el_input, {
              type: "textarea",
              modelValue: formData.value.share_content,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.share_content) = $event)),
              readonly: ""
            }, null, 8, ["modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_form_item, { label: "专题图" }, {
          default: _withCtx(() => [
            _createVNode(_component_el_image, {
              onClick: _cache[3] || (_cache[3] = ($event: any) => (previewList.value = [formData.value.image_path])),
              src: formData.value.image_path,
              "preview-src-list": previewList.value,
              fit: "scale-down",
              "hide-on-click-modal": "",
              "preview-teleported": true,
              class: "w-100"
            }, null, 8, ["src", "preview-src-list"])
          ]),
          _: 1
        }),
        (formData.value.ranking_type !== 1)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(_component_el_card, { shadow: "hover" }, {
                header: _withCtx(() => _cache[20] || (_cache[20] = [
                  _createElementVNode("h3", null, "头部设置", -1)
                ])),
                footer: _withCtx(() => [
                  _createVNode(_component_el_button, {
                    type: "primary",
                    size: "small",
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (addTr('header')))
                  }, {
                    default: _withCtx(() => _cache[23] || (_cache[23] = [
                      _createTextVNode("添加一行")
                    ])),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_el_table, {
                    ref_key: "headerTableRef",
                    ref: headerTableRef,
                    data: formData.value.header,
                    border: "",
                    class: "banner-table",
                    "row-key": "__key"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_table_column, { label: "图片链接" }, {
                        default: _withCtx(({ row }) => [
                          _createVNode(_component_el_input, {
                            modelValue: row.src,
                            "onUpdate:modelValue": ($event: any) => ((row.src) = $event),
                            placeholder: "请输入图片链接"
                          }, null, 8, ["modelValue", "onUpdate:modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, { label: "图片预览" }, {
                        default: _withCtx(({ row }) => [
                          (row.src)
                            ? (_openBlock(), _createBlock(_component_el_image, {
                                key: 0,
                                class: "row-image w-100",
                                onClick: ($event: any) => (previewList.value = [row.src]),
                                src: row.src,
                                "preview-src-list": previewList.value,
                                fit: "scale-down",
                                "hide-on-click-modal": "",
                                "preview-teleported": true
                              }, null, 8, ["onClick", "src", "preview-src-list"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, { label: "跳转地址" }, {
                        default: _withCtx(({ row }) => [
                          _createVNode(_component_el_input, {
                            modelValue: row.url,
                            "onUpdate:modelValue": ($event: any) => ((row.url) = $event)
                          }, null, 8, ["modelValue", "onUpdate:modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "操作",
                        width: "95"
                      }, {
                        default: _withCtx((scope) => [
                          _createVNode(_component_el_button, {
                            type: "text",
                            onClick: ($event: any) => (delTr('header', scope.$index))
                          }, {
                            default: _withCtx(() => _cache[21] || (_cache[21] = [
                              _createTextVNode("删除")
                            ])),
                            _: 2
                          }, 1032, ["onClick"]),
                          _createVNode(_component_el_button, {
                            type: "text",
                            onClick: ($event: any) => (insertTr('header', scope.$index))
                          }, {
                            default: _withCtx(() => _cache[22] || (_cache[22] = [
                              _createTextVNode("插入")
                            ])),
                            _: 2
                          }, 1032, ["onClick"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }, 8, ["data"])
                ]),
                _: 1
              }),
              _createVNode(_component_el_card, {
                shadow: "hover",
                class: "mt20"
              }, {
                header: _withCtx(() => _cache[24] || (_cache[24] = [
                  _createElementVNode("h3", null, "活动设置", -1)
                ])),
                footer: _withCtx(() => [
                  _createVNode(_component_el_button, {
                    type: "primary",
                    size: "small",
                    onClick: _cache[8] || (_cache[8] = ($event: any) => (addTr('activities.contents')))
                  }, {
                    default: _withCtx(() => _cache[28] || (_cache[28] = [
                      _createTextVNode("添加一行")
                    ])),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_el_input, {
                    modelValue: formData.value.delModuleIds,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.value.delModuleIds) = $event)),
                    type: "hidden",
                    name: "delModuleIds"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_el_form_item, { label: "背景图" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_input, {
                        modelValue: formData.value.activities.bg_src,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formData.value.activities.bg_src) = $event))
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_form_item, { label: "背景色" }, {
                    default: _withCtx(() => [
                      _withDirectives((_openBlock(), _createBlock(_component_el_input, {
                        modelValue: formData.value.activities.bgcolor,
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((formData.value.activities.bgcolor) = $event))
                      }, {
                        prepend: _withCtx(() => _cache[25] || (_cache[25] = [
                          _createTextVNode("#")
                        ])),
                        _: 1
                      }, 8, ["modelValue"])), [
                        [_directive_hex_color]
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_table, {
                    ref_key: "activitiesTableRef",
                    ref: activitiesTableRef,
                    data: formData.value.activities.contents,
                    border: "",
                    class: "banner-table",
                    "row-key": "__key"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_table_column, { label: "活动图链接" }, {
                        default: _withCtx(({ row }) => [
                          _createVNode(_component_el_input, {
                            modelValue: row.banner_src,
                            "onUpdate:modelValue": ($event: any) => ((row.banner_src) = $event),
                            placeholder: "请输入活动图链接"
                          }, null, 8, ["modelValue", "onUpdate:modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, { label: "图片预览" }, {
                        default: _withCtx(({ row }) => [
                          (row.banner_src)
                            ? (_openBlock(), _createBlock(_component_el_image, {
                                key: 0,
                                class: "row-image w-100",
                                onClick: ($event: any) => (previewList.value = [row.banner_src]),
                                src: row.banner_src,
                                "preview-src-list": previewList.value,
                                fit: "scale-down",
                                "hide-on-click-modal": "",
                                "preview-teleported": true
                              }, null, 8, ["onClick", "src", "preview-src-list"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, { label: "跳转地址" }, {
                        default: _withCtx(({ row }) => [
                          _createVNode(_component_el_input, {
                            modelValue: row.url,
                            "onUpdate:modelValue": ($event: any) => ((row.url) = $event)
                          }, null, 8, ["modelValue", "onUpdate:modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "操作",
                        width: "95"
                      }, {
                        default: _withCtx((scope) => [
                          _createVNode(_component_el_button, {
                            type: "text",
                            onClick: ($event: any) => (delTr('activities.contents', scope.$index))
                          }, {
                            default: _withCtx(() => _cache[26] || (_cache[26] = [
                              _createTextVNode("删除")
                            ])),
                            _: 2
                          }, 1032, ["onClick"]),
                          _createVNode(_component_el_button, {
                            type: "text",
                            onClick: ($event: any) => (insertTr('activities.contents', scope.$index))
                          }, {
                            default: _withCtx(() => _cache[27] || (_cache[27] = [
                              _createTextVNode("插入")
                            ])),
                            _: 2
                          }, 1032, ["onClick"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }, 8, ["data"])
                ]),
                _: 1
              }),
              _createVNode(_component_el_card, {
                shadow: "hover",
                class: "mt20"
              }, {
                header: _withCtx(() => _cache[29] || (_cache[29] = [
                  _createElementVNode("h3", null, "功能设置", -1)
                ])),
                footer: _withCtx(() => [
                  _createVNode(_component_el_button, {
                    type: "primary",
                    size: "small",
                    onClick: _cache[9] || (_cache[9] = ($event: any) => (addTr('funs')))
                  }, {
                    default: _withCtx(() => _cache[33] || (_cache[33] = [
                      _createTextVNode("添加一行")
                    ])),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_el_table, {
                    ref_key: "funsTableRef",
                    ref: funsTableRef,
                    data: formData.value.funs,
                    border: "",
                    class: "banner-table",
                    "row-key": "__key"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_table_column, { label: "每行切图" }, {
                        default: _withCtx(({ row, $index }) => [
                          _createVNode(_component_el_button, {
                            type: "text",
                            onClick: ($event: any) => (addInnerTr('funs', $index))
                          }, {
                            default: _withCtx(() => _cache[30] || (_cache[30] = [
                              _createTextVNode("添加一张切图")
                            ])),
                            _: 2
                          }, 1032, ["onClick"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, { label: "图片链接" }, {
                        default: _withCtx(({ row }) => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row, (item, idx) => {
                            return (_openBlock(), _createElementBlock("div", { key: idx }, [
                              _createVNode(_component_el_input, {
                                modelValue: item.src,
                                "onUpdate:modelValue": ($event: any) => ((item.src) = $event),
                                class: "src_input",
                                placeholder: "图片链接",
                                style: {"margin-bottom":"10px","width":"90%"}
                              }, null, 8, ["modelValue", "onUpdate:modelValue"])
                            ]))
                          }), 128))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, { label: "图片预览" }, {
                        default: _withCtx(({ row }) => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row, (item, idx) => {
                            return (_openBlock(), _createBlock(_component_el_image, {
                              key: idx,
                              class: "row-image w-100 mb10",
                              onClick: ($event: any) => (previewList.value = [item.src]),
                              src: item.src,
                              "preview-src-list": previewList.value,
                              fit: "scale-down",
                              "hide-on-click-modal": "",
                              "preview-teleported": true
                            }, null, 8, ["onClick", "src", "preview-src-list"]))
                          }), 128))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, { label: "跳转地址" }, {
                        default: _withCtx(({ row }) => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row, (item, idx) => {
                            return (_openBlock(), _createElementBlock("div", { key: idx }, [
                              _createVNode(_component_el_input, {
                                modelValue: item.url,
                                "onUpdate:modelValue": ($event: any) => ((item.url) = $event),
                                placeholder: "输入URL",
                                style: {"margin-bottom":"10px","width":"90%"}
                              }, null, 8, ["modelValue", "onUpdate:modelValue"])
                            ]))
                          }), 128))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, { label: "功能类型" }, {
                        default: _withCtx((scope) => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scope.row, (item, idx) => {
                            return (_openBlock(), _createElementBlock("div", { key: idx }, [
                              _createVNode(_component_el_select, {
                                modelValue: item.coupon_type,
                                "onUpdate:modelValue": ($event: any) => ((item.coupon_type) = $event),
                                class: "coupon_type",
                                style: {"width":"90%","margin-bottom":"10px"}
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_el_option, {
                                    label: "无",
                                    value: "0"
                                  }),
                                  _createVNode(_component_el_option, {
                                    label: "领券",
                                    value: "1"
                                  }),
                                  _createVNode(_component_el_option, {
                                    label: "跳转",
                                    value: "2"
                                  }),
                                  _createVNode(_component_el_option, {
                                    label: "领券并跳转",
                                    value: "12"
                                  })
                                ]),
                                _: 2
                              }, 1032, ["modelValue", "onUpdate:modelValue"])
                            ]))
                          }), 128))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, { label: "券ID" }, {
                        default: _withCtx((scope) => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scope.row, (item, idx) => {
                            return (_openBlock(), _createElementBlock("div", { key: idx }, [
                              _createVNode(_component_el_input, {
                                modelValue: item.coupon_id,
                                "onUpdate:modelValue": ($event: any) => ((item.coupon_id) = $event),
                                class: "coupon_id",
                                style: {"width":"90%","margin-bottom":"10px"},
                                placeholder: "券ID"
                              }, null, 8, ["modelValue", "onUpdate:modelValue"])
                            ]))
                          }), 128))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "操作",
                        width: "95"
                      }, {
                        default: _withCtx((scope) => [
                          _createVNode(_component_el_button, {
                            type: "text",
                            onClick: ($event: any) => (delTr('funs', scope.$index))
                          }, {
                            default: _withCtx(() => _cache[31] || (_cache[31] = [
                              _createTextVNode("删除")
                            ])),
                            _: 2
                          }, 1032, ["onClick"]),
                          _createVNode(_component_el_button, {
                            type: "text",
                            onClick: ($event: any) => (insertTr('funs', scope.$index))
                          }, {
                            default: _withCtx(() => _cache[32] || (_cache[32] = [
                              _createTextVNode("插入")
                            ])),
                            _: 2
                          }, 1032, ["onClick"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }, 8, ["data"])
                ]),
                _: 1
              }),
              _createVNode(_component_el_card, {
                shadow: "hover",
                class: "mt20"
              }, {
                header: _withCtx(() => _cache[34] || (_cache[34] = [
                  _createElementVNode("h3", null, "秒杀设置", -1)
                ])),
                footer: _withCtx(() => [
                  _createVNode(_component_el_button, {
                    type: "primary",
                    size: "small",
                    onClick: _cache[11] || (_cache[11] = ($event: any) => (addTr('spike.products')))
                  }, {
                    default: _withCtx(() => _cache[38] || (_cache[38] = [
                      _createTextVNode("添加一行")
                    ])),
                    _: 1
                  }),
                  _createVNode(_component_el_button, {
                    type: "primary",
                    size: "small",
                    onClick: _cache[12] || (_cache[12] = ($event: any) => (toSecondSort('spike.products')))
                  }, {
                    default: _withCtx(() => _cache[39] || (_cache[39] = [
                      _createTextVNode("一键打乱")
                    ])),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_el_form_item, { label: "背景色" }, {
                    default: _withCtx(() => [
                      _withDirectives((_openBlock(), _createBlock(_component_el_input, {
                        modelValue: formData.value.spike.bgcolor,
                        "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((formData.value.spike.bgcolor) = $event))
                      }, {
                        prepend: _withCtx(() => _cache[35] || (_cache[35] = [
                          _createTextVNode("#")
                        ])),
                        _: 1
                      }, 8, ["modelValue"])), [
                        [_directive_hex_color]
                      ])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_table, {
                    ref_key: "spikeTableRef",
                    ref: spikeTableRef,
                    data: formData.value.spike.products,
                    border: "",
                    class: "limit-table",
                    "row-key": "__key"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_table_column, {
                        label: "品牌",
                        prop: "brand_name"
                      }),
                      _createVNode(_component_el_table_column, { label: "商品ID" }, {
                        default: _withCtx((scope) => [
                          _createVNode(_component_el_input, {
                            modelValue: scope.row.product_id,
                            "onUpdate:modelValue": ($event: any) => ((scope.row.product_id) = $event),
                            class: "product-id",
                            "data-oid": scope.row.product_id,
                            onKeyup: ($event: any) => (onlyNumber(scope.row, 'product_id')),
                            onChange: ($event: any) => (getProductInfo('spike.products', scope.$index, scope.row.product_id)),
                            placeholder: "商品ID"
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "data-oid", "onKeyup", "onChange"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "商品名称",
                        prop: "title"
                      }),
                      _createVNode(_component_el_table_column, { label: "规格" }, {
                        default: _withCtx((scope) => [
                          _createVNode(_component_el_select, {
                            modelValue: scope.row.spec_id,
                            "onUpdate:modelValue": ($event: any) => ((scope.row.spec_id) = $event),
                            onChange: ($event: any) => (onSpecChange(scope.row))
                          }, {
                            default: _withCtx(() => [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scope.row.specs, (spec) => {
                                return (_openBlock(), _createBlock(_component_el_option, {
                                  key: spec.id,
                                  label: spec.name,
                                  value: `${spec.id}`
                                }, null, 8, ["label", "value"]))
                              }), 128))
                            ]),
                            _: 2
                          }, 1032, ["modelValue", "onUpdate:modelValue", "onChange"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "售价",
                        prop: "price"
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "状态",
                        prop: "status"
                      }, {
                        default: _withCtx(({ row }) => [
                          _createTextVNode(_toDisplayString(row.status === 1 ? '上架' : '下架'), 1)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, { label: "图片" }, {
                        default: _withCtx((scope) => [
                          _createVNode(_component_el_input, {
                            modelValue: scope.row.image_path,
                            "onUpdate:modelValue": ($event: any) => ((scope.row.image_path) = $event),
                            class: "src_input",
                            placeholder: "输入图片地址"
                          }, null, 8, ["modelValue", "onUpdate:modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "操作",
                        width: "95"
                      }, {
                        default: _withCtx((scope) => [
                          _createVNode(_component_el_button, {
                            type: "text",
                            onClick: ($event: any) => (delTr('spike.products', scope.$index))
                          }, {
                            default: _withCtx(() => _cache[36] || (_cache[36] = [
                              _createTextVNode("删除")
                            ])),
                            _: 2
                          }, 1032, ["onClick"]),
                          _createVNode(_component_el_button, {
                            type: "text",
                            onClick: ($event: any) => (insertTr('spike.products', scope.$index))
                          }, {
                            default: _withCtx(() => _cache[37] || (_cache[37] = [
                              _createTextVNode("插入")
                            ])),
                            _: 2
                          }, 1032, ["onClick"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }, 8, ["data"])
                ]),
                _: 1
              })
            ], 64))
          : _createCommentVNode("", true),
        _createVNode(_component_el_card, {
          shadow: "hover",
          class: "mt20"
        }, {
          header: _withCtx(() => _cache[40] || (_cache[40] = [
            _createElementVNode("h3", null, "内容设置", -1)
          ])),
          footer: _withCtx(() => [
            _createVNode(_component_el_button, {
              type: "primary",
              size: "small",
              onClick: addModule
            }, {
              default: _withCtx(() => _cache[55] || (_cache[55] = [
                _createTextVNode("添加模块")
              ])),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createVNode(_component_el_input, {
              modelValue: formData.value.delModuleIds,
              "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((formData.value.delModuleIds) = $event)),
              type: "hidden",
              name: "delModuleIds"
            }, null, 8, ["modelValue"]),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formData.value.product_model, (module, idx) => {
              return (_openBlock(), _createElementBlock("div", {
                key: module.module_id || idx,
                class: "module",
                "data-id": module.module_id,
                style: {"position":"relative"}
              }, [
                _createVNode(_component_el_button, {
                  type: "info",
                  circle: "",
                  class: "close",
                  size: "small",
                  onClick: ($event: any) => (delModule(idx,module.module_id))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_icon, null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Close))
                      ]),
                      _: 1
                    })
                  ]),
                  _: 2
                }, 1032, ["onClick"]),
                _createVNode(_component_el_row, {
                  class: "move-btns",
                  justify: "end"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_button, {
                      type: "primary",
                      size: "small",
                      class: "module-up",
                      onClick: ($event: any) => (moveUpModule(idx)),
                      disabled: idx === 0
                    }, {
                      default: _withCtx(() => _cache[41] || (_cache[41] = [
                        _createTextVNode("上移")
                      ])),
                      _: 2
                    }, 1032, ["onClick", "disabled"]),
                    _createVNode(_component_el_button, {
                      type: "primary",
                      size: "small",
                      class: "module-down",
                      style: {"margin-left":"5px"},
                      onClick: ($event: any) => (moveDownModule(idx)),
                      disabled: idx === formData.value.product_model.length - 1
                    }, {
                      default: _withCtx(() => _cache[42] || (_cache[42] = [
                        _createTextVNode(" 下移 ")
                      ])),
                      _: 2
                    }, 1032, ["onClick", "disabled"])
                  ]),
                  _: 2
                }, 1024),
                _cache[54] || (_cache[54] = _createElementVNode("input", {
                  type: "hidden",
                  value: "border"
                }, null, -1)),
                _createVNode(_component_el_input, {
                  modelValue: module.module_id,
                  "onUpdate:modelValue": ($event: any) => ((module.module_id) = $event),
                  type: "hidden",
                  name: "l_id"
                }, null, 8, ["modelValue", "onUpdate:modelValue"]),
                _createVNode(_component_el_form_item, { label: "一行显示几个" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_input, {
                      modelValue: module.col_num,
                      "onUpdate:modelValue": ($event: any) => ((module.col_num) = $event),
                      modelModifiers: { trim: true },
                      class: "col-num",
                      placeholder: "数量"
                    }, null, 8, ["modelValue", "onUpdate:modelValue"]),
                    _cache[43] || (_cache[43] = _createElementVNode("span", { class: "sub" }, "个", -1)),
                    _cache[44] || (_cache[44] = _createElementVNode("span", { style: {"color":"red","margin-left":"10px"} }, "注：设置 -1 为专属模板", -1))
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_component_el_form_item, { label: "背景色" }, {
                  default: _withCtx(() => [
                    _withDirectives((_openBlock(), _createBlock(_component_el_input, {
                      modelValue: module.bgcolor,
                      "onUpdate:modelValue": ($event: any) => ((module.bgcolor) = $event)
                    }, {
                      prepend: _withCtx(() => _cache[45] || (_cache[45] = [
                        _createTextVNode("#")
                      ])),
                      _: 2
                    }, 1032, ["modelValue", "onUpdate:modelValue"])), [
                      [_directive_hex_color]
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_component_el_form_item, { label: "显示文字" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_input, {
                      modelValue: module.text,
                      "onUpdate:modelValue": ($event: any) => ((module.text) = $event),
                      class: "text",
                      style: {"width":"100px"},
                      placeholder: "显示文字"
                    }, null, 8, ["modelValue", "onUpdate:modelValue"])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_component_el_table, {
                  ref_for: true,
                  ref: (el) => setModuleBannerRef(el, idx),
                  data: module.banners,
                  border: "",
                  class: "banner-table",
                  "row-key": "__key"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_table_column, { label: "每行切图" }, {
                      default: _withCtx((scope) => [
                        _createVNode(_component_el_button, {
                          type: "text",
                          style: {"color":"#0a6cd6"},
                          onClick: ($event: any) => (addInnerTr(`product_model.${idx}.banners`, scope.$index))
                        }, {
                          default: _withCtx(() => _cache[46] || (_cache[46] = [
                            _createTextVNode("添加一张切图")
                          ])),
                          _: 2
                        }, 1032, ["onClick"])
                      ]),
                      _: 2
                    }, 1024),
                    _createVNode(_component_el_table_column, { label: "图片链接" }, {
                      default: _withCtx((scope) => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scope.row, (item, bidx) => {
                          return (_openBlock(), _createElementBlock("div", { key: bidx }, [
                            _createVNode(_component_el_input, {
                              modelValue: item.banner_src,
                              "onUpdate:modelValue": ($event: any) => ((item.banner_src) = $event),
                              class: "banner_src_input",
                              placeholder: "图片链接",
                              style: {"width":"90%","margin-bottom":"10px"}
                            }, null, 8, ["modelValue", "onUpdate:modelValue"])
                          ]))
                        }), 128))
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, { label: "图片预览" }, {
                      default: _withCtx((scope) => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scope.row, (item, bidx) => {
                          return (_openBlock(), _createBlock(_component_el_image, {
                            key: bidx,
                            class: "row-image w-100 mb10",
                            onClick: ($event: any) => (previewList.value = [item.banner_src]),
                            src: item.banner_src,
                            "preview-src-list": previewList.value,
                            fit: "scale-down",
                            "hide-on-click-modal": "",
                            "preview-teleported": true
                          }, null, 8, ["onClick", "src", "preview-src-list"]))
                        }), 128))
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, { label: "跳转地址" }, {
                      default: _withCtx((scope) => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scope.row, (item, bidx) => {
                          return (_openBlock(), _createElementBlock("div", { key: bidx }, [
                            _createVNode(_component_el_input, {
                              modelValue: item.banner_url,
                              "onUpdate:modelValue": ($event: any) => ((item.banner_url) = $event),
                              class: "banner_url_input",
                              placeholder: "输入URL",
                              style: {"width":"90%","margin-bottom":"10px"}
                            }, null, 8, ["modelValue", "onUpdate:modelValue"])
                          ]))
                        }), 128))
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "操作",
                      width: "95"
                    }, {
                      default: _withCtx((scope) => [
                        _createVNode(_component_el_button, {
                          type: "text",
                          onClick: ($event: any) => (delBannerTr(module.banners, scope.$index))
                        }, {
                          default: _withCtx(() => _cache[47] || (_cache[47] = [
                            _createTextVNode("删除")
                          ])),
                          _: 2
                        }, 1032, ["onClick"]),
                        _createVNode(_component_el_button, {
                          type: "text",
                          onClick: ($event: any) => (insertBannerTr(module.banners, scope.$index))
                        }, {
                          default: _withCtx(() => _cache[48] || (_cache[48] = [
                            _createTextVNode("插入")
                          ])),
                          _: 2
                        }, 1032, ["onClick"])
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  _: 2
                }, 1032, ["data"]),
                _createVNode(_component_el_row, { justify: "space-between" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_button, {
                      type: "primary",
                      size: "small",
                      onClick: ($event: any) => (addBannerTr(module.banners)),
                      class: "mt20",
                      "data-type": "1"
                    }, {
                      default: _withCtx(() => _cache[49] || (_cache[49] = [
                        _createTextVNode("+ 添加")
                      ])),
                      _: 2
                    }, 1032, ["onClick"]),
                    _createVNode(_component_el_button, {
                      type: "primary",
                      size: "small",
                      onClick: ($event: any) => (onExportProductOrder(module.module_id)),
                      class: "mt20",
                      "data-type": "1"
                    }, {
                      default: _withCtx(() => _cache[50] || (_cache[50] = [
                        _createTextVNode("导出")
                      ])),
                      _: 2
                    }, 1032, ["onClick"])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_component_el_input, {
                  type: "hidden",
                  name: `product${formData.value.isid}[]`,
                  value: "goodsbegin"
                }, null, 8, ["name"]),
                _createVNode(_component_el_table, {
                  ref_for: true,
                  ref: (el) => setModuleProductRef(el, idx),
                  data: module.products,
                  border: "",
                  class: "product-table mt20",
                  "row-key": "__key"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_table_column, {
                      label: "品牌",
                      prop: "brand_name"
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "商品ID",
                      prop: "product_id"
                    }, {
                      default: _withCtx((scope) => [
                        _createVNode(_component_el_input, {
                          modelValue: scope.row.product_id,
                          "onUpdate:modelValue": ($event: any) => ((scope.row.product_id) = $event),
                          class: "product_id_input",
                          onChange: ($event: any) => (getProductInfo(`product_model.${idx}.products`, scope.$index, scope.row.product_id)),
                          placeholder: "输入商品ID"
                        }, null, 8, ["modelValue", "onUpdate:modelValue", "onChange"])
                      ]),
                      _: 2
                    }, 1024),
                    _createVNode(_component_el_table_column, {
                      label: "商品名称",
                      prop: "title"
                    }),
                    (formData.value.topicId === 3284)
                      ? (_openBlock(), _createBlock(_component_el_table_column, {
                          key: 0,
                          label: "规格"
                        }, {
                          default: _withCtx((scope) => [
                            _createVNode(_component_el_select, {
                              modelValue: scope.row.spec_id,
                              "onUpdate:modelValue": ($event: any) => ((scope.row.spec_id) = $event),
                              onChange: ($event: any) => (onSpecChange(scope.row))
                            }, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scope.row.specs, (spec) => {
                                  return (_openBlock(), _createBlock(_component_el_option, {
                                    key: spec.id,
                                    label: spec.name,
                                    value: `${spec.id}`
                                  }, null, 8, ["label", "value"]))
                                }), 128))
                              ]),
                              _: 2
                            }, 1032, ["modelValue", "onUpdate:modelValue", "onChange"])
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    _createVNode(_component_el_table_column, {
                      label: "售价",
                      prop: "price"
                    }),
                    _createVNode(_component_el_table_column, { label: "是否在架" }, {
                      default: _withCtx(({ row }) => [
                        _createTextVNode(_toDisplayString(row.status == 1 ? '上架' : '下架'), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "活动标签",
                      prop: "tag"
                    }, {
                      default: _withCtx((scope) => [
                        (scope.row.tags.length)
                          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(scope.row.tags, (item) => {
                              return (_openBlock(), _createBlock(_component_el_row, { align: "middle" }, {
                                default: _withCtx(() => [
                                  (item.type != 5)
                                    ? (_openBlock(), _createBlock(_component_el_image, {
                                        key: 0,
                                        src: item.img,
                                        style: {"width":"16px","height":"16px","margin-right":"5px"}
                                      }, null, 8, ["src"]))
                                    : _createCommentVNode("", true),
                                  _createVNode(_component_el_text, {
                                    type: "info",
                                    size: "small"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(item.text), 1)
                                    ]),
                                    _: 2
                                  }, 1024)
                                ]),
                                _: 2
                              }, 1024))
                            }), 256))
                          : _createCommentVNode("", true),
                        _createVNode(_component_el_icon, {
                          class: "edit-icon",
                          color: "#409eff",
                          size: 16,
                          onClick: ($event: any) => (openTagDialog(`product_model.${idx}.products.${scope.$index}`, scope.row))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Edit))
                          ]),
                          _: 2
                        }, 1032, ["onClick"])
                      ]),
                      _: 2
                    }, 1024),
                    _createVNode(_component_el_table_column, {
                      label: "操作",
                      width: "55"
                    }, {
                      default: _withCtx((scope) => [
                        _createVNode(_component_el_button, {
                          type: "text",
                          onClick: ($event: any) => (delProductTr(module.products, scope.$index))
                        }, {
                          default: _withCtx(() => _cache[51] || (_cache[51] = [
                            _createTextVNode("删除")
                          ])),
                          _: 2
                        }, 1032, ["onClick"])
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  _: 2
                }, 1032, ["data"]),
                _createVNode(_component_el_row, { class: "mt20" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_button, {
                      type: "primary",
                      size: "small",
                      onClick: ($event: any) => (addTr(`product_model.${idx}.products`))
                    }, {
                      default: _withCtx(() => _cache[52] || (_cache[52] = [
                        _createTextVNode("添加一行")
                      ])),
                      _: 2
                    }, 1032, ["onClick"]),
                    _createVNode(_component_el_button, {
                      type: "primary",
                      size: "small",
                      onClick: ($event: any) => (toSecondSort(`product_model.${idx}.products`))
                    }, {
                      default: _withCtx(() => _cache[53] || (_cache[53] = [
                        _createTextVNode("一键打乱")
                      ])),
                      _: 2
                    }, 1032, ["onClick"])
                  ]),
                  _: 2
                }, 1024)
              ], 8, _hoisted_2))
            }), 128))
          ]),
          _: 1
        }),
        _createVNode(_component_el_card, {
          shadow: "hover",
          class: "mt20"
        }, {
          header: _withCtx(() => _cache[56] || (_cache[56] = [
            _createElementVNode("h3", null, "尾部设置", -1)
          ])),
          footer: _withCtx(() => [
            _createVNode(_component_el_button, {
              type: "primary",
              size: "small",
              onClick: _cache[14] || (_cache[14] = ($event: any) => (addTr('footer')))
            }, {
              default: _withCtx(() => _cache[59] || (_cache[59] = [
                _createTextVNode("添加一行")
              ])),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createVNode(_component_el_table, {
              ref_key: "footerTableRef",
              ref: footerTableRef,
              data: formData.value.footer,
              border: "",
              class: "banner-table",
              "row-key": "__key"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_table_column, { label: "图片链接" }, {
                  default: _withCtx(({ row }) => [
                    _createVNode(_component_el_input, {
                      modelValue: row.src,
                      "onUpdate:modelValue": ($event: any) => ((row.src) = $event),
                      placeholder: "请输入图片链接"
                    }, null, 8, ["modelValue", "onUpdate:modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_table_column, { label: "图片预览" }, {
                  default: _withCtx(({ row }) => [
                    (row.src)
                      ? (_openBlock(), _createBlock(_component_el_image, {
                          key: 0,
                          class: "row-image w-100",
                          onClick: ($event: any) => (previewList.value = [row.src]),
                          src: row.src,
                          "preview-src-list": previewList.value,
                          fit: "scale-down",
                          "hide-on-click-modal": "",
                          "preview-teleported": true
                        }, null, 8, ["onClick", "src", "preview-src-list"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_table_column, { label: "跳转地址" }, {
                  default: _withCtx(({ row }) => [
                    _createVNode(_component_el_input, {
                      modelValue: row.url,
                      "onUpdate:modelValue": ($event: any) => ((row.url) = $event)
                    }, null, 8, ["modelValue", "onUpdate:modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_table_column, {
                  label: "操作",
                  width: "95"
                }, {
                  default: _withCtx((scope) => [
                    _createVNode(_component_el_button, {
                      type: "text",
                      onClick: ($event: any) => (delTr('footer', scope.$index))
                    }, {
                      default: _withCtx(() => _cache[57] || (_cache[57] = [
                        _createTextVNode("删除")
                      ])),
                      _: 2
                    }, 1032, ["onClick"]),
                    _createVNode(_component_el_button, {
                      type: "text",
                      onClick: ($event: any) => (insertTr('footer', scope.$index))
                    }, {
                      default: _withCtx(() => _cache[58] || (_cache[58] = [
                        _createTextVNode("插入")
                      ])),
                      _: 2
                    }, 1032, ["onClick"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["data"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_row, {
          justify: "end",
          class: "mt20"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_button, {
              type: "primary",
              onClick: onSubmit
            }, {
              default: _withCtx(() => _cache[60] || (_cache[60] = [
                _createTextVNode("保存")
              ])),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["model"]),
    _createVNode(_component_el_dialog, {
      modelValue: tagDialogVisible.value,
      "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((tagDialogVisible).value = $event)),
      title: "活动标签",
      width: "500px"
    }, {
      footer: _withCtx(() => [
        _createVNode(_component_el_button, {
          onClick: _cache[15] || (_cache[15] = ($event: any) => {resetTag();tagDialogVisible.value = false})
        }, {
          default: _withCtx(() => _cache[62] || (_cache[62] = [
            _createTextVNode("取消")
          ])),
          _: 1
        }),
        _createVNode(_component_el_button, {
          type: "primary",
          onClick: onCheckedTagsChange
        }, {
          default: _withCtx(() => _cache[63] || (_cache[63] = [
            _createTextVNode("确定")
          ])),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tagList.value, (item) => {
          return (_openBlock(), _createBlock(_component_el_checkbox, {
            class: "mt20",
            modelValue: item.is_checked,
            "onUpdate:modelValue": ($event: any) => ((item.is_checked) = $event),
            key: item.type,
            label: item.type,
            value: item.type
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_row, { style: {"flex-wrap":"nowrap"} }, {
                default: _withCtx(() => [
                  (item.type === 5)
                    ? (_openBlock(), _createBlock(_component_el_text, {
                        key: 0,
                        class: "ml10"
                      }, {
                        default: _withCtx(() => _cache[61] || (_cache[61] = [
                          _createTextVNode("自定义")
                        ])),
                        _: 1
                      }))
                    : (_openBlock(), _createBlock(_component_el_image, {
                        key: 1,
                        src: item.img,
                        alt: item.text,
                        mode: "widthFix",
                        style: {"width":"30px","height":"30px","flex-shrink":"0"},
                        class: "ml10"
                      }, null, 8, ["src", "alt"])),
                  _createVNode(_component_el_input, {
                    modelValue: item.text,
                    "onUpdate:modelValue": ($event: any) => ((item.text) = $event),
                    placeholder: "请输入标签文本",
                    class: "ml10"
                  }, null, 8, ["modelValue", "onUpdate:modelValue"])
                ]),
                _: 2
              }, 1024)
            ]),
            _: 2
          }, 1032, ["modelValue", "onUpdate:modelValue", "label", "value"]))
        }), 128))
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_el_dialog, {
      modelValue: exportProductOrderDialog.value,
      "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((exportProductOrderDialog).value = $event)),
      title: "选择导出订单的日期区间",
      width: "500px"
    }, {
      footer: _withCtx(() => [
        _createVNode(_component_el_button, {
          onClick: _cache[18] || (_cache[18] = ($event: any) => (exportProductOrderDialog.value = false))
        }, {
          default: _withCtx(() => _cache[65] || (_cache[65] = [
            _createTextVNode("取消")
          ])),
          _: 1
        }),
        _createVNode(_component_el_button, {
          type: "primary",
          onClick: exportProductOrder
        }, {
          default: _withCtx(() => _cache[66] || (_cache[66] = [
            _createTextVNode("确定")
          ])),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_form_item, {
          label: "日期区间",
          class: "mt20"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_date_picker, {
              modelValue: exportProductOrderFormData.value.dateRang,
              "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((exportProductOrderFormData.value.dateRang) = $event)),
              type: "daterange",
              "value-format": "YYYY-MM-DD",
              "range-separator": "至",
              "start-placeholder": "开始日期",
              "end-placeholder": "结束日期"
            }, null, 8, ["modelValue"]),
            _cache[64] || (_cache[64] = _createElementVNode("div", {
              class: "color-warning mt10 font12",
              style: {"line-height":"1.4"}
            }, "注意：如果选择了日期区间销售数量列会计算已选日期区间内的销售数量，否则为0", -1))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})